import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, API, uploadFile } from "@/lib/api";

const CONFIG = {
  fornitori: {
    title: "Fornitore",
    fields: [
      { key: "ragione_sociale", label: "Ragione Sociale", required: true },
      { key: "piva", label: "Partita IVA" },
      { key: "categoria", label: "Categoria (es. Materiali, Subappalti)" },
      { key: "telefono", label: "Telefono" },
      { key: "email", label: "Email" },
      { key: "indirizzo", label: "Indirizzo" },
      { key: "citta", label: "Città" },
      { key: "cap", label: "CAP" },
      { key: "provincia", label: "Provincia" },
    ],
  },
  operai: {
    title: "Operaio",
    fields: [
      { key: "nome", label: "Nome e Cognome", required: true },
      { key: "mansione", label: "Mansione" },
      { key: "telefono", label: "Telefono" },
      { key: "paga_oraria", label: "Paga Oraria (€)", type: "number" },
    ],
  },
  mezzi: {
    title: "Mezzo",
    fields: [
      { key: "nome", label: "Nome / Targa", required: true },
      { key: "tipo", label: "Tipo (es. Escavatore, Furgone)" },
      { key: "stato", label: "Stato", type: "stato" },
      { key: "costo_orario", label: "Costo Orario Totale (€)", type: "number" },
      { key: "scadenza_revisione", label: "Scadenza Revisione", type: "date" },
      { key: "scadenza_assicurazione", label: "Scadenza Assicurazione", type: "date" },
    ],
  },
  direttori: {
    title: "Direttore di cantiere",
    fields: [
      { key: "nome", label: "Nome e Cognome", required: true },
    ],
  },
};

const EMPTY = {
  ragione_sociale: "", piva: "", categoria: "", telefono: "", email: "",
  indirizzo: "", citta: "", cap: "", provincia: "",
  nome: "", mansione: "", paga_oraria: "", tipo: "", stato: "disponibile", costo_orario: "",
  scadenza_revisione: "", scadenza_assicurazione: "", certificazioni: [], firma_file_id: "", note: "",
};

export default function ArchivioModal({ open, onOpenChange, tipo, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cfg = CONFIG[tipo];
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { ...EMPTY, ...initial, paga_oraria: String(initial.paga_oraria ?? ""), costo_orario: String(initial.costo_orario ?? ""), certificazioni: initial.certificazioni || [] }
        : EMPTY);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const req = cfg.fields.find((f) => f.required);
    if (req && !form[req.key]?.trim()) return toast.error(`Inserisci ${req.label}.`);

    const payload = {};
    cfg.fields.forEach((f) => {
      payload[f.key] = f.type === "number" ? (Number(form[f.key]) || 0) : (form[f.key] || "");
    });
    payload.note = form.note || "";
    if (tipo === "operai") {
      payload.certificazioni = (form.certificazioni || []).filter((c) => c.nome && c.scadenza);
    }
    if (tipo === "direttori") {
      payload.firma_file_id = form.firma_file_id || "";
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/${tipo}/${initial.id}`, payload);
        toast.success(`${cfg.title} aggiornato.`);
      } else {
        await api.post(`/${tipo}`, payload);
        toast.success(`${cfg.title} aggiunto.`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="archivio-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica ${cfg.title}` : `Nuovo ${cfg.title}`}</DialogTitle>
          <DialogDescription>Compila i dati e salva nell'archivio aziendale.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {cfg.fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "stato" ? (
                <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
                  <SelectTrigger data-testid="mezzo-stato-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponibile">Disponibile</SelectItem>
                    <SelectItem value="in_uso">In uso</SelectItem>
                    <SelectItem value="manutenzione">In manutenzione</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="0.01" min="0"
                  data-testid={`archivio-${f.key}-input`} value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)} />
              )}
            </div>
          ))}
          {tipo === "operai" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Certificazioni / Corsi (con scadenza)</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1" data-testid="add-cert-row"
                  onClick={() => setForm((f) => ({ ...f, certificazioni: [...(f.certificazioni || []), { nome: "", scadenza: "" }] }))}>
                  <Plus className="h-3.5 w-3.5" /> Aggiungi
                </Button>
              </div>
              {(form.certificazioni || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna certificazione.</p>}
              {(form.certificazioni || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Es. Sicurezza 81/08, Visita medica" data-testid={`cert-nome-${i}`} value={c.nome}
                    onChange={(e) => setForm((f) => ({ ...f, certificazioni: f.certificazioni.map((r, idx) => idx === i ? { ...r, nome: e.target.value } : r) }))} />
                  <Input type="date" className="w-40" data-testid={`cert-scadenza-${i}`} value={c.scadenza}
                    onChange={(e) => setForm((f) => ({ ...f, certificazioni: f.certificazioni.map((r, idx) => idx === i ? { ...r, scadenza: e.target.value } : r) }))} />
                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    onClick={() => setForm((f) => ({ ...f, certificazioni: f.certificazioni.filter((_, idx) => idx !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
          {tipo === "direttori" && (
            <div className="space-y-2">
              <Label>Firma del direttore</Label>
              <p className="text-xs text-muted-foreground">Carica un'immagine della firma (JPG/PNG/WEBP). Verrà stampata nei PDF ODA/CEL firmati.</p>
              {form.firma_file_id ? (
                <div className="flex items-center gap-3">
                  <img src={`${API}/uploads/${form.firma_file_id}`} alt="firma" className="h-14 max-w-[180px] object-contain rounded border bg-white p-1" data-testid="direttore-firma-preview" />
                  <Button type="button" variant="ghost" size="sm" className="text-destructive" data-testid="direttore-firma-remove" onClick={() => set("firma_file_id", "")}>Rimuovi</Button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer text-sm rounded-md border border-dashed px-3 py-2 hover:bg-secondary w-fit" data-testid="direttore-firma-upload">
                  <Upload className="h-4 w-4" />
                  {uploading ? "Caricamento..." : "Carica firma"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      setUploading(true);
                      try { const res = await uploadFile(file); set("firma_file_id", res.file_id); toast.success("Firma caricata."); }
                      catch (err) { toast.error(err.message || "Caricamento non riuscito."); }
                      finally { setUploading(false); e.target.value = ""; }
                    }} />
                </label>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea rows={2} data-testid="archivio-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="archivio-cancel-button">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="archivio-save-button"
            className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
